import { useCallback } from "react";
import SntHideLongText from "@/components/ReactBootstrap/SntHideLongText";

import {
  AssetTag,
  DangerTag,
  DefaultTag,
  GreyTag,
  PrimaryTag,
  SecondaryTag,
  SuccessTag,
  WarningTag,
} from "@cleanup/components/TagMore";
import SntCheckBox from "@cleanup/components/SntCheckBox/SntCheckBox";
import SmartAssetTypePath from "@/cleanup/widgets/AssetTypePicker/SmartAssetTypePath";
import { ASSET_TYPE_PATH_SEPARATOR } from "@/cleanup/widgets/AssetTypePicker/assetTypePath";
import { FilterOptionHint } from "./FilterStyle";

interface CheckboxItemProps {
  optionHint?: number | string;
  label: string | React.ReactNode;
  onChange: (value: boolean) => void;
  checked: boolean;
  cssClass?: string;
}

const CheckboxItem: React.FC<CheckboxItemProps> = ({
  optionHint,
  label,
  onChange,
  checked,
  cssClass,
}) => {
  return (
    <div className="d-flex justify-content-between mb-2">
      <SntCheckBox
        checked={checked}
        onChange={(value) => onChange(value)}
        label={
          <CheckboxFilterTag value={cssClass} title={label}></CheckboxFilterTag>
        }
      />

      <FilterOptionHint>{optionHint}</FilterOptionHint>
    </div>
  );
};
export default CheckboxItem;

interface CheckboxFilterTagProps {
  value?: string;
  other?: string;
  title: string | React.ReactNode;
}

const CheckboxFilterTag: React.FC<CheckboxFilterTagProps> = ({
  value,
  other,
  title,
}) => {
  const render = useCallback(
    (value?: string) => {
      // If title is not a string, render it directly
      if (typeof title !== 'string') {
        return <>{title}</>;
      }

      switch (value) {
        case "label label-success":
        case "label label-stop":
          return <SuccessTag tagName={title} />;
        case "label label-orange":
        case "label label-warning":
          return <WarningTag tagName={title} />;
        case "label label-danger":
          return <DangerTag tagName={title} />;
        case "label label-default":
          return <GreyTag tagName={title} />;
        case "label label-primary":
          return <SecondaryTag tagName={title} />;
        case "label label-blue":
        case "label label-dark":
          return <PrimaryTag tagName={title} />;
        case "asset-tag-render":
          return <AssetTag tagName={title} />;
        case "asset-type-path-render":
          // Asset-type filter options: the SAME blue chip (PrimaryTag) as the display chips, with
          // the smart, level-collapsing path (root > ... > leaf) so the leaf stays visible —
          // instead of a plain end-ellipsis that hides it.
          return (
            <PrimaryTag title={title}>
              <SmartAssetTypePath
                segments={title.split(ASSET_TYPE_PATH_SEPARATOR)}
                maxWidth={174}
                title={title}
              />
            </PrimaryTag>
          );
        case undefined:
          return (
            <SntHideLongText
              text={title}
              style={{ maxWidth: "180px" }}
              className="d-inline-block align-middle"
            ></SntHideLongText>
          );
        default:
          return <DefaultTag tagName={title || other} />;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, title]
  );

  return <>{render(value)}</>;
};
